import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Eye, PlayCircle, Swords } from "lucide-react";
import { useLocation } from "wouter";

// Define the props interface for the component
interface MatchCardProps {
  match: {
    id: number;
    name?: string;
    gameType: string;
    createdAt: string;
    player1Info?: { id: string; username: string };
    player2Info?: { id: string; username: string };
    player1Score: number;
    player2Score: number;
    winner: string;
  };
  onReplayMatch?: (id: number) => void;
  showHeader?: boolean;
  className?: string;
}

export function MatchCard({ 
  match, 
  onReplayMatch,
  showHeader = false,
  className = ""
}: MatchCardProps) {
  const [_, setLocation] = useLocation();
  
  const handleReplayMatch = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onReplayMatch) {
      onReplayMatch(match.id);
    }
  };
  
  const handleViewDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLocation(`/game-history/${match.id}`);
  };
  
  return (
    <>
      {showHeader && (
        <h3 className="text-base font-medium flex items-center gap-2 mb-3 text-white/80">
          <Clock className="h-4 w-4 text-primary" />
          Your Last Match
        </h3>
      )}
      <Card
        className={`overflow-hidden backdrop-blur-sm border border-white/5 transition-all duration-300
        cursor-pointer hover:border-primary/30 bg-gradient-to-r from-[#111827] to-[#0c111d] ${className}`}
        onClick={() => setLocation(`/game-history/${match.id}`)}
      >
        <CardHeader className="px-4 py-3 border-b border-white/5 bg-black/30 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Swords className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm font-medium">
              {match.name ?? "Match"}
            </CardTitle>
            <Badge
              variant="outline"
              className={`text-xs py-0 h-5 bg-black/40 border-white/10
              ${match.gameType === "ranked" ? "text-yellow-400" : "text-white/70"}`}
            >
                {match.gameType === "ranked" 
                ? "Ranked" 
                : match.gameType === "quick"
                ? "Quick"
                : match.gameType === "tournament"
                ? "Tournament"
                : match.gameType === "ai"
                ? "Practice"
                : "Casual"}
            </Badge>
          </div>
          <div className="text-xs text-white/50">
            {new Date(match.createdAt).toLocaleDateString(
              undefined,
              {
                month: "short",
                day: "numeric",
              },
            )}
          </div>
        </CardHeader>
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col items-center">
              <div className="text-sm font-medium">
                {match.player1Info?.username}
              </div>
              <div
                className={`text-xl font-bold ${match.winner === match.player1Info?.id ? "text-primary" : ""}`}
              >
                {match.player1Score}
              </div>
            </div>

            <div className="px-4 py-1 rounded-full bg-black/30 text-xs font-medium">
              vs
            </div>

            <div className="flex flex-col items-center">
              <div className="text-sm font-medium">
                {match.player2Info?.username}
              </div>
              <div
                className={`text-xl font-bold ${match.winner === match.player2Info?.id ? "text-primary" : ""}`}
              >
                {match.player2Score}
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-2 border-t border-white/5 bg-black/20 flex justify-between">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-white/70 hover:text-white hover:bg-white/5 px-2"
            onClick={handleReplayMatch}
          >
            <PlayCircle className="h-3 w-3 mr-1" />
            Replay Match
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-white/70 hover:text-white hover:bg-white/5 px-2"
            onClick={handleViewDetails}
          >
            <Eye className="h-3 w-3 mr-1" />
            View Details
          </Button>
        </div>
      </Card>
    </>
  );
}